<template>
  <side-panel-inner
    :on-close="onCloseAction"
    headerTitle="Document Preview"
    v-if="documentReady && document.id"
    is-wide
  >
    <template slot="actions" v-if="!document.archived">
      <div v-if="$route.meta.isAdmin">
        <dropdown>
          <dropdown-action
            icon="edit"
            @click="onClickEdit"
            v-if="!$route.params.versionId"
            >Edit</dropdown-action
          >
          <dropdown-action icon="cross" @click="onReject"
            >Reject</dropdown-action
          >
          <dropdown-action icon="tick" @click="onApprove"
            >Approve</dropdown-action
          >
          <dropdown-action icon="download" @click="onClickDownload"
            >Download</dropdown-action
          >
          <dropdown-action icon="share" @click="addQrCode = true"
            >Generate QR Code</dropdown-action
          >
          <dropdown-action
            icon="edit"
            @click="onClickCreateVersion"
            v-if="!$route.params.versionId"
            >Create new version</dropdown-action
          >
          <dropdown-action
            icon="archive"
            @click="onClickArchive"
            v-if="hasPermissionToUpdate && document && isApproved"
            >Archive</dropdown-action
          >
        </dropdown>
      </div>
      <div v-else>
        <base-button
          :primary="true"
          iconBefore="download"
          v-if="file && !document.current_version.external_url && isApproved"
          @click="onClickDownload"
          >Download</base-button
        >
        <base-button
          :primary="true"
          iconBefore="edit"
          v-if="isOwner && isRejected"
          @click="onClickEdit"
          >Resubmit</base-button
        >
      </div>
    </template>

    <document-meta
      :document="document"
      :highlight="true"
      :showFull="true"
    ></document-meta>

    <download-warning :isStatic="true" v-if="!document.archived" />
    <callout
      type="warning"
      v-if="!hasPermissionToUpdate && !hideWarning && !document.archived"
    >
      <p>
        To make amendments to this {{ singular }}, please contact the
        Responsible Contacts listed above.
      </p>
    </callout>
    <hr />

    <div v-if="file && fileReady">
      <pdf-preview
        v-if="file.mime_type == 'application/pdf'"
        :fileId="file.id"
      ></pdf-preview>
    </div>
    <div v-else-if="externalLink">
      <p>This form is hosted on an external web address.</p>
      <p>
        <a :href="externalLink" target="_blank"
          >Click here to view the document.</a
        >
      </p>
    </div>
    <loading v-else></loading>
    <modal
      v-if="addQrCode"
      show-close-button
      @close="addQrCode = false"
      class="ta-left"
    >
      <h1 slot="header">Create QR Code</h1>
      <qr-generator />
    </modal>
  </side-panel-inner>

  <side-panel-inner v-else>
    <loading></loading>
  </side-panel-inner>
</template>

<script>
import PdfPreview from "@/components/PdfPreview";
import Dropdown from "@/components/Dropdown";
import DropdownAction from "@/components/DropdownAction";
import Callout from "@/components/Containers/BaseCallout";
import DownloadWarning from "@/components/DownloadWarning";
import { Download } from "./../../mixins/download";
import QrGenerator from "@/components/contentElements/modals/TheQrGenerator";
export default {
  name: "document-preview",
  mixins: [Download],

  data() {
    return {
      documentReady: false,
      fileReady: false,
      downloadPercent: 0,
      hideWarning: false,
      prevUrl: null,
      addQrCode: false,
    };
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevUrl = from.fullPath;
    });
  },

  beforeMount() {
    const vm = this;
    const documentId = this.$route.params.id;
    const versionId = this.$route.params.versionId;

    // so that the background isn't empty...
    // only if we are on a documents page
    if (this.prevUrl !== null && this.prevUrl.includes("/documents/")) {
      if (
        !this.$store.state.documents[this.$store.state.documents.docType.plural]
      ) {
        this.$store.dispatch("documents/list");
      }
    }

    // if we are previewing a document, not in admin, and there is a redirect, then redirect
    // todo: should it just be archived regardless?

    this.$store.dispatch("documents/read", documentId).then(async (resp) => {
      if (this.prevUrl !== null && !this.prevUrl.includes("/documents/")) {
        if (resp.archived == 1) {
          if (resp.final_archive_redirect_policy_id) {
            await this.$store.dispatch(
              "documents/read",
              resp.final_archive_redirect_policy_id
            );
          }
        }
      }
      vm.documentReady = true;
      vm.fileReady = true;
    });

    if (documentId && versionId) {
      this.$store
        .dispatch("documentVersions/read", { documentId, versionId })
        .then(() => {
          vm.documentReady = true;
        });
    }
  },

  computed: {
    currentLink() {
      return window.location.href;
    },
    onCloseAction() {
      if (this.prevUrl != null && this.prevUrl.includes("?")) {
        return this.prevUrl;
      }
      if (this.prevUrl != null && this.prevUrl.includes("/documents/")) {
        if (this.$route.meta.isAdmin) {
          return `/admin/documents/${this.$store.state.documents.docType.plural}`;
        }
        return `/documents/${this.$store.state.documents.docType.plural}`;
      }
      if (this.prevUrl) {
        return this.prevUrl;
      }
      return `/documents/${this.$store.state.documents.docType.plural}`;
    },
    isOwner() {
      return (
        this.document.created_user.id === this.$store.state.auth.currentUser.id
      );
    },
    isRejected() {
      const { currentDocument } = this.$store.state.documents;
      if (!!currentDocument.current_version === false) {
        return true;
      }

      return currentDocument.current_version.status === "rejected";
    },
    isApproved() {
      const { currentDocument } = this.$store.state.documents;

      if (!!currentDocument.current_version === false) {
        return false;
      }

      return currentDocument.current_version.status === "approved";
    },
    document() {
      if (this.$route.params.versionId) {
        return {
          ...this.$store.state.documentVersions.single,
          tags: this.$store.state.documents.currentDocument.tags,
        };
      }
      return this.$store.state.documents.currentDocument;
    },

    externalLink() {
      if (this.$store.state.documents.currentVersion) {
        return this.$store.state.documents.currentVersion.external_url;
      }
      return (
        this.$store.state.documents.currentDocument.current_version
          .external_url || null
      );
    },

    file() {
      if (this.$route.params.versionId) {
        return this.$store.state.documentVersions.single.file;
      }
      return this.$store.state.documents.currentDocument.current_version.file;
    },

    singular() {
      return this.$store.state.documents.docType.singular;
    },

    hasPermissionToUpdate() {
      if (!this.$store.state.auth.currentUser.privileges) {
        this.$store.dispatch("auth/listCurrentUser").then((resp) => {
          return this.$store.state.auth.currentUser.privileges[
            `add_${this.$route.params.docType}`
          ];
        });
      } else {
        return this.$store.state.auth.currentUser.privileges[
          `add_${this.$route.params.docType}`
        ];
      }
    },
  },

  methods: {
    onClickArchive() {
      this.$router.push({
        name: "AdminDocumentArchive",
        params: { id: this.document.id },
      });
    },
    onClickShare() {},
    onClickDownload() {
      this.$store.dispatch("files/download", this.file);
      // this.showWarning();
    },
    onClickCreateVersion() {
      this.$router.push({
        name: "AdminDocumentCreateVersion",
        params: { id: this.$route.params.id },
      });
    },

    onApprove() {
      const documentId = this.$route.params.id;
      const versionId = this.$route.params.versionId;
      this.$router.push({
        name: "AdminDocumentVersionApprove",
        params: { id: documentId, versionId },
      });
    },

    onReject() {
      const documentId = this.$route.params.id;
      const versionId = this.$route.params.versionId
        ? this.$route.params.versionId
        : this.document.current_version.id;
      this.$router.push({
        name: "AdminDocumentVersionReject",
        params: {
          docType: this.$route.params.docType,
          id: documentId,
          versionId,
        },
      });
    },
    onClickEdit() {
      const documentId = this.$route.params.id;
      this.$router.push({
        name: "AdminDocumentEdit",
        params: { documentId: documentId },
      });
    },
  },

  components: {
    PdfPreview,
    Dropdown,
    DropdownAction,
    Callout,
    DownloadWarning,
    QrGenerator,
  },
};
</script>
